/**
 * Certificates Hooks - React Query hooks for TLS/SSL Certificates
 * 
 * Provides dropdown-compatible hooks for selecting certificates
 * in forms like SipInterfacesPage
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';

/**
 * Certificate type for dropdown selection
 */
export interface CertificateOption {
  id: string;
  certificateId: string;
  name: string;
  description?: string;
  certificateType: 'server' | 'client' | 'ca' | 'intermediate';
  subjectDn?: string;
  validFrom?: string;
  validTo?: string;
  teamsCompatible?: boolean;
}

interface CertificatesResponse {
  success: boolean;
  data: CertificateOption[];
  timestamp: string;
}

/**
 * Fetch all certificates - useful for dropdown selectors
 */
export function useCertificates(params?: {
  certificateType?: CertificateOption['certificateType'];
}) {
  return useQuery({
    queryKey: ['certificates', params],
    queryFn: async (): Promise<CertificatesResponse> => {
      const response = await api.getCertificates(params);
      return {
        success: true,
        data: (response.data || []).map(cert => ({ ...cert, id: cert.certificateId, description: cert.description ?? undefined })),
        timestamp: response.timestamp
      };
    },
    staleTime: 60000, // Consider data fresh for 1 minute
  });
}

/**
 * Fetch certificates formatted for dropdown selection
 * Returns only active certificates with id and name
 */
export function useCertificatesForDropdown(certificateType?: CertificateOption['certificateType']) {
  return useQuery({
    queryKey: ['certificates-dropdown', certificateType],
    queryFn: async () => {
      const response = await api.getCertificates({
        certificateType
      });
      const certs: Array<{
        id?: string;
        certificateId?: string;
        name?: string;
        description?: string | null;
        certificateType?: string;
        teamsCompatible?: boolean;
        validFrom?: string;
        validTo?: string;
      }> = response.data || [];
      const now = Date.now();
      return certs
        .filter((cert) => {
          if (!cert.validTo || !cert.validFrom) return false;
          const validFrom = new Date(cert.validFrom).getTime();
          const validTo = new Date(cert.validTo).getTime();
          return validFrom <= now && validTo > now;
        })
        .map((cert) => ({
        value: cert.certificateId || cert.id,
        label: cert.name,
        description: cert.description,
        certificateType: cert.certificateType,
        teamsCompatible: cert.teamsCompatible,
        validTo: cert.validTo,
      }));
    },
    staleTime: 60000,
  });
}

/**
 * Fetch server certificates for TLS interfaces
 */
export function useServerCertificatesForDropdown() {
  return useCertificatesForDropdown('server');
}

/**
 * Fetch CA certificates for client verification
 */
export function useCACertificatesForDropdown() {
  return useCertificatesForDropdown('ca');
}

/**
 * Fetch single certificate by ID
 */
export function useCertificate(certificateId: string | null | undefined) {
  return useQuery({
    queryKey: ['certificate', certificateId],
    queryFn: async () => {
      if (!certificateId) return null;
      const response = await api.getCertificateById(certificateId);
      return response.data;
    },
    enabled: !!certificateId,
  });
}
